import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';

export default function MarketAnalysisChatView({ analysisId, onComplete, hideSteps = false }) {
  const [status, setStatus] = useState('processing');
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const steps = [
    { name: 'Analyzing query...', icon: 'fa-magnifying-glass-chart' },
    { name: 'Searching web...', icon: 'fa-globe' },
    { name: 'Generating pdf...', icon: 'fa-file-pdf' },
  ];

  useEffect(() => {
    let pollInterval;
    let progressInterval;

    const checkStatus = async () => {
      try {
        const res = await axios.get(`/market-analysis/${analysisId}`);
        if (res.data.success) {
          const data = res.data.data;
          if (data.status === 'completed') {
            setStatus('completed');
            setResult(data.analysis_result);
            if (onComplete) onComplete(data);
            return true; // Finished
          } else if (data.status === 'failed') {
            setStatus('failed');
            setError(data.analysis_result?.error || 'Analysis failed.');
            return true; // Finished
          }
        }
      } catch (err) {
        console.error('Status check error:', err.message);
        if (err.response?.status === 403 || err.response?.status === 404) {
          setStatus('failed');
          setError(
            err.response?.status === 403 ? 'Analysis access forbidden.' : 'Analysis not found.'
          );
          return true; // Stop polling on terminal error
        }
      }
      return false; // Still processing
    };

    if (analysisId && status === 'processing') {
      // Immediate check first
      checkStatus().then((finished) => {
        if (finished) return;

        // Only start intervals if not finished
        progressInterval = setInterval(() => {
          setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
        }, 10000); // Step every 10s

        pollInterval = setInterval(async () => {
          const done = await checkStatus();
          if (done) {
            clearInterval(pollInterval);
            clearInterval(progressInterval);
          }
        }, 5000);
      });
    }

    return () => {
      clearInterval(pollInterval);
      clearInterval(progressInterval);
    };
  }, [analysisId]);

  const handleDownload = async () => {
    try {
      setDownloadError(null);
      const response = await axios.get(`/market-analysis/${analysisId}/download/full`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename =
        result?.pdfs?.fullReport?.filename ||
        `Market-Research_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setDownloadError('Failed to download PDF.');
    }
  };

  if (status === 'processing' && !hideSteps) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', opacity: 0.5 }}>
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p style={{ marginTop: '10px' }}>Analyzing market data...</p>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        style={{
          padding: '24px',
          color: '#ef4444',
          background: '#fef2f2',
          borderRadius: '16px',
          border: '1px solid #fecaca',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <i className="fas fa-exclamation-circle fa-lg"></i>
        <div style={{ fontSize: '14px', fontWeight: '500' }}>{error}</div>
      </div>
    );
  }

  if (status === 'completed' && result) {
    return (
      <div
        style={{
          padding: '16px 0 0 0',
          background: 'transparent',
          borderRadius: '0',
          border: 'none',
          boxShadow: 'none',
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
            <strong>Time Used:</strong> {result.summary?.processingTime || 'Under 2m'}
          </div>
          <div style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
            <strong>Resources Used:</strong> {result.summary?.totalSourcesAnalyzed || 0} web sources
          </div>
        </div>

        <button
          onClick={handleDownload}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            padding: '16px',
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#e2e8f0')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#f1f5f9')}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              background: '#ef4444',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <i className="fas fa-file-pdf fa-lg"></i>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
              {result?.pdfs?.fullReport?.filename || 'Market-Research_Report.pdf'}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Full Analytical Report</div>
          </div>
          <i className="fas fa-download" style={{ color: '#64748b' }}></i>
        </button>
        {downloadError && (
          <div
            style={{
              marginTop: '12px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            {downloadError}
          </div>
        )}
      </div>
    );
  }

  return null;
}
